import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Radio, Plus, Trash2, ExternalLink, Users, Copy, Check, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import LivecastWizard from "@/components/dashboard/LivecastWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const PUBLIC = `${process.env.REACT_APP_BACKEND_URL}`;

export default function LivecastsPage() {
  const [data, setData] = useState({ livecasts: [], access: false, businesses: [] });
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/livecasts/me`, { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      toast.error("Couldn't load LiveCasts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this LiveCast? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/livecasts/${id}`, { headers: authHeaders() });
      toast.success("Deleted");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onEdit = (lc) => {
    setEditing(lc);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setEditing(null);
  };

  if (loading) return <div className="p-8 text-[#94A3B8]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="livecasts-page">
      <header>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Radio className="w-6 h-6 text-[#F4B41A]" /> LiveCast™
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">
          Create scheduled or watch-now AI Horse-Powered events that live directly on your LocalBest.ai listing.
        </p>
      </header>

      {!data.access ? (
        <LockedCard />
      ) : (
        <>
          <div className="flex justify-end">
            <Button
              onClick={() => setWizardOpen(true)}
              className="bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:opacity-90 text-[#0A0F1C] font-semibold rounded-full"
              data-testid="livecast-new-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New LiveCast
            </Button>
          </div>

          {data.livecasts.length === 0 ? (
            <EmptyState onCreate={() => setWizardOpen(true)} />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {data.livecasts.map((lc) => (
                <LivecastCard
                  key={lc.id}
                  item={lc}
                  onDelete={() => onDelete(lc.id)}
                  onEdit={() => onEdit(lc)}
                  onRefresh={fetchData}
                />
              ))}
            </div>
          )}
        </>
      )}

      {wizardOpen && (
        <LivecastWizard
          businesses={data.businesses}
          initial={editing}
          onClose={closeWizard}
          onCreated={() => { closeWizard(); fetchData(); }}
        />
      )}
    </div>
  );
}

function authHeaders() {
  const t = localStorage.getItem("auth_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function LockedCard() {
  return (
    <div className="rounded-2xl border border-[#F4B41A]/30 bg-gradient-to-br from-[#F4B41A]/[0.08] via-[#0A0F1C] to-[#1E40AF]/[0.08] p-8 text-center" data-testid="livecast-locked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#F4B41A] to-[#E0A015] flex items-center justify-center mb-4 shadow-[0_0_30px_rgba(244,180,26,0.4)]">
        <Radio className="w-7 h-7 text-[#0A0F1C]" />
      </div>
      <h2 className="text-xl font-bold text-white mb-2">Create your listing to unlock LiveCast™</h2>
      <p className="text-[#C7D2E0] max-w-xl mx-auto leading-relaxed">
        LiveCast™ is now included free with every LocalBest.ai account. As soon as you create or claim your listing,
        you can schedule AI Horse-Powered events that live directly on your profile and turn your business into a
        24/7 lead engine.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/dashboard/listing">
          <Button className="bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:opacity-90 text-[#0A0F1C] font-bold rounded-full" data-testid="livecast-locked-create-listing">
            <Plus className="w-4 h-4 mr-2" /> Create my listing
          </Button>
        </Link>
        <Link to="/get-funded">
          <Button variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full">
            Talk to our team
          </Button>
        </Link>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
      <Radio className="w-12 h-12 mx-auto text-[#F4B41A]/60 mb-4" />
      <h3 className="text-white text-lg font-semibold mb-1">No LiveCasts yet</h3>
      <p className="text-[#94A3B8] mb-5">Create your first event and publish it directly to your listing.</p>
      <Button onClick={onCreate} className="bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:opacity-90 text-[#0A0F1C] font-semibold rounded-full" data-testid="livecast-empty-create">
        <Plus className="w-4 h-4 mr-1.5" /> Create LiveCast
      </Button>
    </div>
  );
}

function LivecastCard({ item, onDelete, onEdit, onRefresh }) {
  const [copied, setCopied] = useState(false);
  const publicUrl = item.public_url ? `${window.location.origin}${item.public_url}` : null;
  const thumb = item?.video?.thumbnail;

  const togglePublish = async () => {
    try {
      await axios.patch(`${API}/livecasts/${item.id}`, { publish: !item.published }, { headers: authHeaders() });
      toast.success(item.published ? "Unpublished" : "Published to listing");
      onRefresh();
    } catch {
      toast.error("Update failed");
    }
  };

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-[#F4B41A]/40 transition-colors" data-testid={`livecast-card-${item.slug}`}>
      <div className="aspect-video bg-black relative">
        {thumb ? (
          <img src={thumb} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1E293B] to-[#0F172A]">
            <Radio className="w-10 h-10 text-[#F4B41A]/40" />
          </div>
        )}
        <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${item.published ? "bg-[#F4B41A] text-[#0A0F1C]" : "bg-white/15 text-white"}`}>
          {item.published ? "Published" : "Draft"}
        </span>
        <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-black/60 backdrop-blur text-white">
          {item.type.replace("_", " ")}
        </span>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <h4 className="text-white font-semibold leading-tight">{item.title}</h4>
          <p className="text-xs text-[#94A3B8] mt-0.5">on {item.business_name}</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-[#94A3B8]">
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {item.registrants_count || 0} registrants</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onEdit}
            className="border-[#F4B41A]/40 bg-[#F4B41A]/10 hover:bg-[#F4B41A]/20 text-[#F4B41A]"
            data-testid={`livecast-edit-${item.slug}`}
          >
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          <Button size="sm" onClick={togglePublish} className={item.published ? "bg-white/10 hover:bg-white/15 text-white" : "bg-[#F4B41A] hover:bg-[#E0A015] text-[#0A0F1C] font-semibold"}>
            {item.published ? "Unpublish" : "Publish"}
          </Button>
          {publicUrl && (
            <>
              <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                <Button size="sm" variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-white">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> View
                </Button>
              </a>
              <Button size="sm" variant="outline" onClick={copyLink} className="border-white/15 bg-white/5 hover:bg-white/10 text-white">
                {copied ? <Check className="w-3.5 h-3.5 mr-1 text-green-400" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                {copied ? "Copied" : "Copy link"}
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={onDelete} className="border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 ml-auto">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
